import { Link } from "react-router-dom";
import { ExternalLink, Landmark, ShieldCheck } from "lucide-react";
import { formatIDR } from "@kanata/shared";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

/**
 * The hand-off screen shown before a buyer leaves for the payment partner's
 * page.
 *
 * # Why this exists
 *
 * QRIS is branded and stays on kanata.id. Virtual account is not — paying by VA
 * still means a hand-off, and that is not a slip: virtual-account direct charge
 * does not exist at any layer of the payment stack yet, and it additionally
 * needs per-bank enablement whose lead time is not ours to control.
 *
 * So this release ships a mixed experience, and the job of this screen is to
 * make that read as deliberate rather than broken. Without it, a buyer picking
 * VA gets exactly the thing the client complained about, sitting right next to
 * the thing we just fixed — which looks worse than before we started.
 *
 * # When VA direct charge lands
 *
 * Delete this file and the one branch in Payment.tsx that renders it. Nothing
 * else changes. That is the whole reason it is a separate component and not
 * three conditions inside the payment page.
 *
 * # A note on the copy
 *
 * It says "mitra pembayaran" and never names a company, because a buyer who
 * came to buy a desk does not need our vendor list, and a shop that recites its
 * architecture on the way to a payment page sounds like a status page. Same
 * reason there is no mention of why QRIS is different.
 */
export function VaHandoff({
  orderNumber,
  totalIdr,
  redirectUrl,
  /**
   * The method the buyer actually chose. Usually VA — but if the branded QRIS
   * switch has been turned off, a QRIS buyer arrives here too, and telling them
   * they are going to fetch a virtual account number would be a lie.
   */
  method,
}: {
  orderNumber: string;
  totalIdr: number;
  redirectUrl: string;
  method?: string;
}) {
  const isVA = method !== "QRIS";

  return (
    <div className="rounded-2xl border border-ink/8 bg-cream/60 p-6 sm:p-8">
      <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-brand-light text-brand-dark">
        <Landmark className="size-6" />
      </div>

      <h2 className="mt-4 text-center text-2xl font-medium tracking-tight text-ink">
        {isVA ? "Ambil nomor virtual account" : "Lanjut ke halaman pembayaran"}
      </h2>
      <p className="mx-auto mt-2 max-w-sm text-center text-sm leading-relaxed text-ink/60">
        {isVA ? (
          <>
            Kami akan membawa kamu ke halaman mitra pembayaran Kanata untuk mendapatkan nomor
            virtual account-nya. Setelah itu tinggal transfer dari m-banking seperti biasa.
          </>
        ) : (
          <>
            Kami akan membawa kamu ke halaman mitra pembayaran Kanata untuk menyelesaikan
            pembayaran ini.
          </>
        )}
      </p>

      {/* What they are agreeing to, before they go. A hand-off that arrives
          without warning is the part that feels broken — not the hand-off. */}
      <div className="mt-6 rounded-xl border border-ink/8 bg-white/60 px-5 py-4">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-sm text-ink/55">Nomor pesanan</span>
          <span className="text-sm font-medium text-ink">{orderNumber}</span>
        </div>
        <Separator className="my-3" />
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-sm text-ink/55">Total</span>
          <span className="text-lg font-medium text-ink tabular-nums">{formatIDR(totalIdr)}</span>
        </div>
      </div>

      <Button asChild size="lg" className="mt-6 w-full">
        {/* A full navigation in the same tab, not a new window: the payment page
            IS the next step, and a popup gets blocked or lost behind the
            browser — on a phone, permanently. */}
        <a href={redirectUrl}>
          Lanjut ke pembayaran
          <ExternalLink className="size-4" />
        </a>
      </Button>

      {/* The way home. A buyer who closes the tab mid-transfer has no route
          back unless we give them one here, before they leave. */}
      <p className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-ink/50">
        <ShieldCheck className="mt-0.5 size-3.5 shrink-0" />
        <span>
          Catat nomor pesanannya ya. Kamu bisa kembali dan cek status pembayaran kapan saja di{" "}
          <Link to="/cek-pesanan" className="text-brand underline underline-offset-4">
            cek pesanan
          </Link>{" "}
          dengan nomor pesanan dan nomor HP kamu.
        </span>
      </p>
    </div>
  );
}
